import React, { useEffect, useState } from 'react';
import reverse from 'ramda/src/reverse';
import sortBy from 'ramda/src/sortBy';
import { getOrUndefined, map } from 'util/resource';
import { useUserProfiles } from 'apis/CompanyAPI/users/useUserProfiles';
import MessageField from 'domain/conversations/Message/MessageField';
import Resource from 'util/resource/Resource';
import Messages from './Message/Messages';
import { PostConversationMessage, UserConversation } from 'apis/CompanyAPI/conversations/conversationApi';
import { UserProfile } from 'types/user';

import { useConversationById, useConversationByUsers } from 'apis/CompanyAPI/conversations/useConversationById';
import { useConversationMessages } from 'apis/CompanyAPI/conversations/useConversationMessages';
import { useConversationAttachments } from 'apis/CompanyAPI/conversations/useConversationAttachments';
import { useConversationActions } from 'apis/CompanyAPI/conversations/useConversationActions';
import { notEmpty } from 'util/arrayUtils';
import InfoIcon from 'ui/elements/icons/InfoIcon';
import ConversationHeader from './Message/ConversationHeader';
import { getConversationName } from './utils';
import Avatars from 'domain/conversations/Avatars';
import UserSearch from 'domain/users/UserSearch';
import cx from 'classnames';
import UserProfileDialog from 'domain/users/UserProfile/UserProfileDialog';
import ConversationSettingsButton from './Message/ConversationSettingsButton';
import { SharedContent } from './Message/types';
import { usersApi } from 'apis/CompanyAPI/users/usersApi';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import { uniqBy } from 'ramda';
import UserConversationIntroduction from './UserConversationIntroduction';
import useNotify from 'hooks/useNotify';
import { SendAsChoice } from './sendAs';

export type ConversationType =
  | {
      type: 'new';
    }
  | {
      type: 'withUsers';
      withCwUserIds: UUID[];
    }
  | {
      type: 'existing';
      conversation: UserConversation;
    };

export interface Props {
  conversation: ConversationType;
  closeConversation: () => void;
  closeInbox?: () => void;
  groupName?: string;
  sharedContent?: SharedContent;
  initialMessage?: string;
  /** Set when opened from the inbox, so the picker can hand you back to the entity's thread. */
  sendAs?: SendAsChoice;
}

function getConversationMembers(conversation: ConversationType, user?: UserProfile) {
  if (conversation.type === 'new') return [];
  else if (conversation.type === 'existing')
    return conversation.conversation.userConversation.activeParticipants
      .concat(conversation.conversation.userConversation.inactiveParticipants)
      .filter(u => u !== user?.cwUserId);
  else return conversation.withCwUserIds;
}

function getUserProfilesByIds(cwUserIds: string[], userProfiles: UserProfile[]) {
  return cwUserIds.map(u => userProfiles.find(up => up.cwUserId === u)).filter(notEmpty);
}

export default function UserConversationDialog(props: Props) {
  const [sharedContent, setSharedContent] = useState(props.sharedContent);
  const notify = useNotify();

  const { createConversation, sendMessage, approveConversationRequest, rejectConversationRequest } =
    useConversationActions();

  const [currentConversation, setCurrentConversation] = useState(props.conversation);

  const [hasConversationStarted, setHasConversationStarted] = useState(currentConversation.type !== 'new');
  const [conversationJustCreated, setConversationJustCreated] = useState(false);

  const { resource: userProfileResource } = useSelfUserProfile();
  const userProfile = getOrUndefined(userProfileResource);

  const [showSidebar, setShowSidebar] = useState(false);

  const [withCwUserIds, setWithCwUserIds] = useState<UUID[]>(getConversationMembers(currentConversation, userProfile));

  // Collect all user IDs that need to be loaded
  const allNeededUserIds = React.useMemo(() => {
    const ids: string[] = [...withCwUserIds];

    if (currentConversation.type === 'existing') {
      ids.push(...currentConversation.conversation.userConversation.activeParticipants);
      ids.push(...currentConversation.conversation.userConversation.inactiveParticipants);
    } else if (currentConversation.type === 'withUsers') {
      ids.push(...currentConversation.withCwUserIds);
    }

    return Array.from(new Set(ids)); // Remove duplicates
  }, [
    withCwUserIds.join(','), // Stable string representation
    currentConversation.type,
    currentConversation.type === 'existing'
      ? currentConversation.conversation.id // Use ID instead of whole object
      : currentConversation.type === 'withUsers'
        ? currentConversation.withCwUserIds.join(',')
        : null,
  ]);

  const userProfiles = useUserProfiles(allNeededUserIds);
  const [originalMembers, setOriginalMembers] = useState<UserProfile[]>(
    getUserProfilesByIds(getConversationMembers(currentConversation, userProfile), userProfiles),
  );

  const withUserProfiles = getUserProfilesByIds(withCwUserIds, userProfiles);

  const [notConnectedUsers, setNotConnectedUsers] = useState<UserProfile[] | null>(null);

  function getConversationActiveMembers(user: UserProfile, activeMembers: UserProfile[]): UserProfile[] {
    return activeMembers.length > 0
      ? activeMembers.map(m => m.cwUserId).includes(user.cwUserId)
        ? uniqBy(
            (user: UserProfile) => user.cwUserId,
            [user, ...activeMembers.filter(m => m.cwUserId !== user.cwUserId)],
          )
        : uniqBy(
            (user: UserProfile) => user.cwUserId,
            activeMembers.filter(m => m.cwUserId !== user.cwUserId),
          )
      : [user];
  }

  function getConversationPendingMembers(user: UserProfile, pendingMembers: UserProfile[]) {
    if (pendingMembers.length > 0) {
      if (pendingMembers.map(m => m.cwUserId).includes(user.cwUserId)) {
        return uniqBy(
          (user: UserProfile) => user.cwUserId,
          [user, ...pendingMembers.filter(m => m.cwUserId !== user.cwUserId)],
        );
      } else {
        return uniqBy(
          (user: UserProfile) => user.cwUserId,
          pendingMembers.filter(m => m.cwUserId !== user.cwUserId),
        );
      }
    } else {
      return [];
    }
  }

  const [conversationName, setConversationName] = useState(
    currentConversation.type === 'existing' ? currentConversation.conversation.name : props.groupName,
  );

  const [userDialogProfile, setUserDialogProfile] = useState<UserProfile>();

  // Always call both hooks to maintain hooks order (Rules of Hooks)
  const { resource: conversationByIdResource } = useConversationById(
    currentConversation.type === 'existing' ? currentConversation.conversation.id : undefined,
  );
  const { resource: conversationByUsersResource } = useConversationByUsers(
    currentConversation.type === 'existing' ? [] : withCwUserIds,
  );

  // Extract conversation from the appropriate resource based on conversation type
  const conversationResource =
    currentConversation.type === 'existing' ? conversationByIdResource : conversationByUsersResource;
  const conversation = getOrUndefined(conversationResource) as UserConversation | undefined;

  // Update conversation name when conversation loads
  useEffect(() => {
    if (conversation?.name) {
      setConversationName(conversation.name);
    }
  }, [conversation]);

  // Only update originalMembers when conversation changes or needed profiles are loaded
  const conversationMemberIds = React.useMemo(
    () => getConversationMembers(currentConversation, userProfile),
    [currentConversation, userProfile],
  );

  const neededProfilesLoaded = React.useMemo(() => {
    const profiles = getUserProfilesByIds(conversationMemberIds, userProfiles);
    return profiles.length === conversationMemberIds.length; // All needed profiles loaded
  }, [conversationMemberIds, userProfiles]);

  useEffect(() => {
    if (neededProfilesLoaded && hasConversationStarted) {
      setOriginalMembers(getUserProfilesByIds(conversationMemberIds, userProfiles));
    }
  }, [hasConversationStarted, conversationMemberIds, neededProfilesLoaded]); // Smart dependency!

  const withActiveUserProfiles = conversation?.userConversation.activeParticipants
    ? getUserProfilesByIds(conversation?.userConversation.activeParticipants, userProfiles)
    : [];
  const withPendingUserProfiles = conversation?.userConversation.inactiveParticipants
    ? getUserProfilesByIds(conversation?.userConversation.inactiveParticipants, userProfiles)
    : [];

  // Determine if we should wait for conversation to load before showing messages
  // Don't wait if conversation check returned 404 (no conversation exists - that's fine)
  const isWaitingForConversation =
    conversationResource.state === 'fetching' ||
    (conversationResource.state === 'idle' && currentConversation.type !== 'new') ||
    (conversationResource.state === 'error' &&
      conversationResource.errorType !== 'NotFound' &&
      currentConversation.type !== 'new');

  // Check if this is a brand new conversation (not started) or just created
  const isNewOrJustCreated = (currentConversation.type === 'new' && !hasConversationStarted) || conversationJustCreated;

  const [messagesResource, reloadMessages, seeMoreMessages] = useConversationMessages(
    conversation?.id || (currentConversation.type === 'existing' ? currentConversation.conversation.id : undefined),
    30,
    isWaitingForConversation, // Pass loading state
    isNewOrJustCreated, // Is this a new conversation that hasn't started yet, or just created?
  );

  // Clear the "just created" flag once messages successfully load
  useEffect(() => {
    if (
      conversationJustCreated &&
      messagesResource.state === 'fetched' &&
      messagesResource.resource.values.length > 0
    ) {
      setConversationJustCreated(false);
    }
  }, [
    conversationJustCreated,
    messagesResource.state,
    messagesResource.state === 'fetched' && messagesResource.resource.values.length,
  ]);

  // Load attachments using SWR hook
  const { resource: attachmentResource } = useConversationAttachments(conversation?.id);

  const updateConversationName = (name?: string) => {
    setConversationName(name);
    // Note: Cache invalidation happens automatically through SWR
    // The conversation name will be updated when the API is called
  };

  const onClickUser = () => setUserDialogProfile(withUserProfiles[0]);

  const title = conversationName ? (
    <span className="u-wrap-text">{conversationName}</span>
  ) : withUserProfiles.length === 1 ? (
    <div className="u-wrap-text">
      <span onClick={onClickUser} style={{ cursor: 'pointer' }}>
        {getConversationName(withUserProfiles)}
      </span>
    </div>
  ) : (
    <span className="u-wrap-text">{getConversationName(withUserProfiles)}</span>
  );

  const onReply = async (message: PostConversationMessage) => {
    if (conversation) {
      // Send message to existing conversation
      const result = await sendMessage({ conversationId: conversation.id, message });
      setSharedContent(undefined);
      return result;
    } else {
      // Create new conversation
      const result = await createConversation({
        withUsers: withCwUserIds,
        message,
        name: props.groupName,
        onSuccess: result => {
          setCurrentConversation({ type: 'existing', conversation: result });
          setConversationJustCreated(true); // Mark that we just created this conversation
        },
      });
      if (!hasConversationStarted) {
        setHasConversationStarted(true);
      }

      setSharedContent(undefined);
      return result;
    }
  };

  const onApproveRequest = async () => {
    if (conversation?.id) {
      try {
        await approveConversationRequest(conversation.id);
        // The conversation will be updated automatically through cache invalidation
        if (conversation) {
          setCurrentConversation({ type: 'existing', conversation });
        }
      } catch {
        notify('error', 'Failed to accept the conversation request');
      }
    }
  };

  const onRejectRequest = async () => {
    if (conversation?.id) {
      try {
        await rejectConversationRequest(conversation.id);
        props.closeConversation();
      } catch {
        notify('error', 'Failed to decline the conversation request');
      }
    }
  };

  const messages = map(messagesResource, r => ({
    ...r,
    values: sortBy(
      a => new Date(a.createdAt),
      reverse(r.values).map(c => ({
        ...c,
        align: userProfile?.cwUserId === c.creatorCwUserId ? ('right' as const) : ('left' as const),
      })),
    ),
  }));

  const handleEditingMemberList = () => {
    // Preserve current members before switching to edit mode
    const currentMemberIds = getConversationMembers(currentConversation, userProfile);
    const currentMembers = getUserProfilesByIds(currentMemberIds, userProfiles);
    // Only preserve if we have all the profiles loaded
    if (currentMembers.length === currentMemberIds.length && currentMembers.length > 0) {
      setOriginalMembers(currentMembers);
    }
    setHasConversationStarted(false);
    setConversationName('');
  };

  async function checkConnectionToUser(cwUserIds: string[], currentUserProfiles: UserProfile[]) {
    if (cwUserIds.length) {
      const isConnectedRequests = cwUserIds.map(async id => ({
        id,
        isConnected: await usersApi.isConnected(id),
      }));
      const isConnectedResponses = await Promise.all(isConnectedRequests);

      const notConnectedUserIds = isConnectedResponses.filter(r => !r.isConnected).map(r => r.id);
      const notConnectedUserProfiles = getUserProfilesByIds(notConnectedUserIds, currentUserProfiles);

      setNotConnectedUsers(notConnectedUserProfiles);
    } else {
      setNotConnectedUsers([]);
    }
  }

  useEffect(() => {
    if (userProfiles.length > 0 && withCwUserIds.length > 0) {
      checkConnectionToUser(withCwUserIds, userProfiles);
    } else if (withCwUserIds.length === 0) {
      setNotConnectedUsers([]);
    }
  }, [withCwUserIds, userProfiles]);

  const onChangeConversationMembers = async (userIds: string[]) => {
    setCurrentConversation({ type: 'new' });
    setWithCwUserIds(userIds);
  };

  const conversationIsWithSelf =
    currentConversation.type === 'withUsers' &&
    currentConversation.withCwUserIds.every(id => id === userProfile?.cwUserId);

  const isConversationPending =
    conversation &&
    userProfile &&
    ((conversation.userConversation.inactiveParticipants.includes(userProfile.cwUserId) &&
      !conversation.userConversation.rejectedAt) ||
      (conversation.userConversation.activeParticipants.includes(userProfile.cwUserId) &&
        conversation.userConversation.activeParticipants.length < 2));

  // Show not connected warning outside of Resource wrapper so it appears even when user profile is loading
  const notConnectedWarning = (() => {
    const hasUnconnectedUsers = notConnectedUsers !== null && notConnectedUsers.length > 0;
    return hasUnconnectedUsers ? (
      <UserConversationIntroduction members={withUserProfiles} notConnectedMembers={notConnectedUsers} />
    ) : null;
  })();

  return (
    <>
      {/* Show not connected warning immediately, outside of Resource wrapper */}
      {notConnectedWarning}

      <Resource resource={userProfileResource}>
        {userProfile => (
          <Messages
            attachments={attachmentResource}
            addMessage={() => reloadMessages()}
            introduction={
              // Only show introduction if there are actually unconnected users to warn about
              // or if this is an existing conversation that has started
              notConnectedUsers !== null && (notConnectedUsers.length > 0 || hasConversationStarted) ? (
                <UserConversationIntroduction members={withUserProfiles} notConnectedMembers={notConnectedUsers} />
              ) : undefined
            }
            conversationId={conversation?.id}
            conversationName={conversationName}
            setConversationName={updateConversationName}
            setEditingMemberList={handleEditingMemberList}
            user={userProfile}
            activeMembers={withCwUserIds.length > 1 ? getConversationActiveMembers(userProfile, withActiveUserProfiles) : undefined}
            pendingMembers={getConversationPendingMembers(userProfile, withPendingUserProfiles)}
            userProfiles={userProfiles}
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
            onClose={props.closeConversation}
            onNavigate={props.closeInbox}
            enableConversationNameChange={
              withCwUserIds.length > 1 &&
              conversation?.userConversation.activeParticipants.includes(userProfile.cwUserId)
            }
            showApproval={
              conversation?.userConversation.inactiveParticipants.includes(userProfile.cwUserId) &&
              !conversation.userConversation.rejectedAt
            }
            onApproveRequest={onApproveRequest}
            onRejectRequest={onRejectRequest}
            header={
              !hasConversationStarted ? (
                <>
                  <span className="u-half-spacing-right text-weight-medium">To:</span>
                  <UserSearch
                    initialUsers={originalMembers}
                    sharedCompanyId={sharedContent?.type === 'company' ? sharedContent.companyId : undefined}
                    onChange={onChangeConversationMembers}
                    noResultsText={
                      props.sharedContent?.type === 'company'
                        ? 'No results. Companies can only be shared with community members.'
                        : 'No results'
                    }
                  />
                </>
              ) : (
                <ConversationHeader
                  title={title}
                  logo={<Avatars users={withUserProfiles} />}
                  suffix={
                    <ConversationSettingsButton
                      onClick={() => setShowSidebar(!showSidebar)}
                      text={showSidebar ? 'Close Settings' : 'Show Settings'}
                    />
                  }
                  onClickUser={!conversationName && withUserProfiles.length === 1 ? onClickUser : undefined}
                />
              )
            }
            conversation={messages}
            seeMore={seeMoreMessages}
          >
            <>
              {conversationIsWithSelf ? (
                <p className="u-align-center u-content-padding">
                  <InfoIcon className="u-half-spacing-right"></InfoIcon>You&apos;re not allowed to write to yourself
                </p>
              ) : (
                <MessageField
                  placeholder={
                    isConversationPending
                      ? 'Conversation is pending. The recipient has to accept the request before you can send more messages.'
                      : 'Enter your message...'
                  }
                  handleSubmit={onReply}
                  autoFocus={currentConversation.type !== 'new'}
                  submitClassName={cx(
                    'data-track-conversation-send-message',
                    'data-track-conversation-send-message-as-user',
                  )}
                  disabled={withCwUserIds.length < 1 || isConversationPending}
                  isConversationPending={isConversationPending}
                  padding="none"
                  sendAs={props.sendAs}
                  sharedContent={sharedContent}
                  characterLimit={
                    conversation?.id || (notConnectedUsers && notConnectedUsers.length === 0) ? undefined : 300
                  }
                  initialMessage={props.initialMessage}
                />
              )}
            </>
          </Messages>
        )}
      </Resource>
      <UserProfileDialog
        isOpen={!!userDialogProfile}
        cwUserId={userDialogProfile?.cwUserId}
        onClose={() => setUserDialogProfile(undefined)}
      />
    </>
  );
}
